class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.cartDrawerPrimeTimer = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  clearCartDrawerOpeningState() {
    document.documentElement.classList.remove('pb-cart-drawer-opening');
    document.body.classList.remove('pb-cart-drawer-opening');
  }

  getElementAttributeText(element, attribute) {
    if (!(element instanceof Element) || typeof element.getAttribute !== 'function') return '';
    return String(element.getAttribute(attribute) || '');
  }

  getElementLabelText(element) {
    if (!(element instanceof Element)) return '';
    return [
      this.getElementAttributeText(element, 'id'),
      this.getElementAttributeText(element, 'class'),
      this.getElementAttributeText(element, 'aria-label'),
      this.getElementAttributeText(element, 'data-testid'),
      this.getElementAttributeText(element, 'title'),
      element.textContent ? String(element.textContent) : '',
    ].join(' ');
  }

  shouldPreservePageScroll() {
    return (
      document.body.classList.contains('pb-trust-funnel-v1-active') ||
      document.body.classList.contains('pb-home-index-active') ||
      document.documentElement.classList.contains('pb-home-index-active') ||
      Boolean(document.querySelector('[data-pb-trust-funnel-v1]'))
    );
  }

  setCartDrawerOpenState(open = true) {
    window.clearTimeout(this.cartDrawerPrimeTimer);
    document.documentElement.classList.toggle('pb-cart-drawer-open', open);
    document.documentElement.classList.remove('pb-cart-drawer-opening');
    document.body.classList.toggle('pb-cart-drawer-open', open);
    document.body.classList.remove('pb-cart-drawer-opening');

  }

  isCartDrawerBlockingFloaters() {
    return (
      document.body.classList.contains('pb-cart-drawer-open') ||
      document.body.classList.contains('pb-cart-drawer-opening') ||
      document.documentElement.classList.contains('pb-cart-drawer-open') ||
      document.documentElement.classList.contains('pb-cart-drawer-opening')
    );
  }

  primeCartDrawerOpenState() {
    window.clearTimeout(this.cartDrawerPrimeTimer);
    document.documentElement.classList.add('pb-cart-drawer-opening');
    document.body.classList.add('pb-cart-drawer-opening');

    this.cartDrawerPrimeTimer = window.setTimeout(() => {
      if (this.classList.contains('active') || this.hasAttribute('open')) return;
      this.clearCartDrawerOpeningState();
    }, 1200);
  }

  startExternalFloatingSuppression() {
    this.stopExternalFloatingSuppression();

    this.externalFloatingSuppressionInterval = window.setInterval(() => {
      if (!this.isCartDrawerBlockingFloaters()) {
        this.stopExternalFloatingSuppression();
        return;
      }
      this.setExternalFloatingVisibility(true);
    }, 350);

    if (!('MutationObserver' in window) || !document.body) return;

    this.externalFloatingSuppressionObserver = new MutationObserver(() => {
      if (!this.isCartDrawerBlockingFloaters()) return;
      window.requestAnimationFrame(() => this.setExternalFloatingVisibility(true));
    });
    this.externalFloatingSuppressionObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-label', 'class', 'id', 'style'],
      childList: true,
      subtree: true,
    });
  }

  stopExternalFloatingSuppression() {
    if (this.externalFloatingSuppressionInterval) {
      window.clearInterval(this.externalFloatingSuppressionInterval);
      this.externalFloatingSuppressionInterval = null;
    }

    if (this.externalFloatingSuppressionObserver) {
      this.externalFloatingSuppressionObserver.disconnect();
      this.externalFloatingSuppressionObserver = null;
    }
  }

  isExternalFloatingSuppressionTarget(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.matches('pandectes-cmp')) return false;
    if (
      element.matches('shopify-agent, shopify-chat') ||
      element.closest('shopify-agent, shopify-chat')
    )
      return false;

    const tagName = element.tagName;
    if (['HTML', 'BODY', 'HEAD', 'SCRIPT', 'STYLE', 'META', 'LINK'].includes(tagName)) return false;
    if (this.isMerchantWidget(element)) {
      this.cleanMerchantWidget(element);
      return false;
    }

    if (
      element === this ||
      element.closest(
        'cart-drawer, cart-notification, .cart-drawer, .drawer, .shopify-section-header, .header-wrapper, header, .shopify-section-header-sticky'
      )
    )
      return false;

    return true;
  }

  repairRootFloatingSuppressionState() {
    [document.documentElement, document.body].forEach((element) => {
      if (!element) return;
      element.removeAttribute('data-pb-floating-suppressed');
      element.style.removeProperty('opacity');
      element.style.removeProperty('visibility');
      element.style.removeProperty('pointer-events');
      element.style.removeProperty('display');
      element.style.removeProperty('transform');
    });
  }

  isMerchantWidget(element) {
    if (!(element instanceof HTMLElement)) return false;
    const id = this.getElementAttributeText(element, 'id').toLowerCase();
    const src = this.getElementAttributeText(element, 'src').toLowerCase();
    const name = this.getElementAttributeText(element, 'name').toLowerCase();
    return Boolean(
      id.includes('merchantwidget') ||
        id.includes('merchant-center') ||
        src.includes('merchantwidget') ||
        src.includes('customer-reviews') ||
        name.includes('merchant') ||
        element.closest('#google-merchantwidget-iframe-wrapper, [id*="merchantwidget" i], [id*="merchant-center" i]')
    );
  }

  cleanMerchantWidget(element) {
    if (!(element instanceof HTMLElement)) return;
    element.removeAttribute('data-pb-external-floating');
    element.removeAttribute('data-pb-home-chat');
    element.removeAttribute('data-pb-home-chat-icon');
    element.removeAttribute('data-pb-home-support-floating');
    element.removeAttribute('data-pb-home-consent');
    element.removeAttribute('data-pb-floating-suppressed');
    element.setAttribute('data-pb-google-merchant-widget', 'true');
  }

  getExternalFloatingElements() {
    const drawerBlocking = this.isCartDrawerBlockingFloaters();
    const selectors = [
      '#ShopifyChat',
      'inbox-online-store-chat#ShopifyChat',
      'inbox-online-store-chat',
      'iframe[title*="chat" i]',
      'iframe[name*="chat" i]',
      'iframe[src*="chat" i]',
      'iframe[title*="message" i]',
      'iframe[name*="message" i]',
      'iframe[src*="message" i]',
      'iframe[title*="mesaj" i]',
      'iframe[name*="mesaj" i]',
      'iframe[src*="mesaj" i]',
      'iframe[src*="inbox" i]',
      'iframe[src*="whatsapp" i]',
      'iframe[src*="crisp" i]',
      'iframe[src*="intercom" i]',
      'iframe[src*="tidio" i]',
      '[id*="ShopifyChat"]',
      '[id*="shopifychat" i]',
      '[id*="shopify-chat" i]',
      '[class*="ShopifyChat"]',
      '[class*="shopifychat" i]',
      '[class*="shopify-chat"]',
      '[class*="chat-widget"]',
      '[class*="ChatWidget"]',
      '[class*="chatWidget" i]',
      '[aria-label*="Chat"]',
      '[aria-label*="chat"]',
      '.pb-wa-btn',
      '[href*="wa.me"]',
      '[href*="whatsapp"]',
      '[class*="whatsapp"]',
      '[class*="WhatsApp"]',
      '#pb-cookie-bar',
      '#pb-prefs-modal',
      '[data-pb-home-chat="true"]',
      '[data-pb-home-chat-icon="true"]',
      '[data-pb-home-support-floating="true"]',
      '[data-pb-home-consent="true"]',
      '[data-pb-floating-suppressed="true"]',
      '[data-pb-tf-cookie-panel]',
      '.pb-sticky-atc',
      '[data-pb-sticky-atc]',
      '.product-sticky-form',
      '[id*="inbox" i]',
      '[class*="inbox" i]',
      '[id*="message" i]',
      '[aria-label*="message" i]',
      '[class*="message" i]',
      '[data-testid*="message" i]',
      '[id*="mesaj" i]',
      '[aria-label*="mesaj" i]',
      '[class*="mesaj" i]',
      '[data-testid*="mesaj" i]',
      '[id*="cookie" i]',
      '[class*="cookie" i]',
      '[id*="consent" i]',
      '[class*="consent" i]',
      '[id*="support" i]',
      '[class*="support" i]',
      '[id*="suport" i]',
      '[class*="suport" i]',
      '[class*="crisp"]',
      '[class*="intercom"]',
      '[class*="tidio"]',
      '[data-testid*="chat"]',
    ];
    const elements = selectors.flatMap((selector) => {
      try {
        return Array.from(document.querySelectorAll(selector));
      } catch (error) {
        return [];
      }
    }).filter((element) => this.isExternalFloatingSuppressionTarget(element));

    Array.from(document.querySelectorAll('body *')).forEach((element) => {
      if (!this.isExternalFloatingSuppressionTarget(element)) return;

      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;

      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const zIndex = Number.parseInt(style.zIndex || '0', 10);
      const label = this.getElementLabelText(element).toLowerCase();
      const fixedElement = style.position === 'fixed';
      const highEnough = Number.isNaN(zIndex) || zIndex >= 5 || fixedElement;
      const compact =
        rect.width <= Math.min(window.innerWidth * 0.98, 860) &&
        rect.height <= Math.min(window.innerHeight * 0.86, 760);
      const nearBottom = window.innerHeight - rect.bottom <= 520 || rect.top >= window.innerHeight * 0.3;
      const nearLeft = rect.left <= 300 || rect.right < window.innerWidth * 0.5;
      const nearRight = window.innerWidth - rect.right <= 380 || rect.left > window.innerWidth * 0.5;
      const nearViewportEdge = nearBottom || nearLeft || nearRight || rect.top <= 120;
      const looksLikeExternalWidget =
        /chat|message|mesaj|whatsapp|cookie|consim|accept|confiden|support|suport|gdpr|pandectes|tidio|crisp|intercom|inbox/.test(
          label
        );

      const compactFloatingBubble =
        fixedElement &&
        rect.width >= 38 &&
        rect.height >= 38 &&
        rect.width <= 220 &&
        rect.height <= 170 &&
        (window.innerWidth - rect.right <= 120 || rect.left <= 140) &&
        window.innerHeight - rect.bottom <= 280;

      if (
        highEnough &&
        compact &&
        (drawerBlocking ||
          looksLikeExternalWidget ||
          compactFloatingBubble ||
          (nearBottom && (nearLeft || nearRight)) ||
          (nearViewportEdge && /wa|chat|message|mesaj/.test(label)))
      ) {
        if (window.innerWidth - rect.right <= 120) {
          element.setAttribute('data-pb-external-floating', 'chat');
          element.setAttribute('data-pb-home-chat-icon', 'true');
        } else if (/cookie|consim|accept|gdpr|pandectes|confiden/.test(label)) {
          element.setAttribute('data-pb-external-floating', 'consent');
          element.setAttribute('data-pb-home-consent', 'true');
        } else if (rect.left <= 140) {
          element.setAttribute('data-pb-external-floating', 'support');
          element.setAttribute('data-pb-home-support-floating', 'true');
        }
        element.setAttribute('data-pb-floating-suppressed', 'true');
        elements.push(element);
      }
    });

    return Array.from(new Set(elements)).filter(Boolean);
  }

  setExternalFloatingVisibility(open) {
    this.repairRootFloatingSuppressionState();
    this.externalFloatingStyleCache = this.externalFloatingStyleCache || new Map();
    const elements = this.getExternalFloatingElements();

    if (open) {
      elements.forEach((element) => {
        if (!this.isExternalFloatingSuppressionTarget(element)) return;
        if (!this.externalFloatingStyleCache.has(element)) {
          this.externalFloatingStyleCache.set(element, {
            style: element.getAttribute('style') || '',
            suppressed: element.getAttribute('data-pb-floating-suppressed') || '',
          });
        }
        element.setAttribute('data-pb-floating-suppressed', 'true');
        element.style.setProperty('opacity', '0', 'important');
        element.style.setProperty('visibility', 'hidden', 'important');
        element.style.setProperty('pointer-events', 'none', 'important');
        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('transform', 'translateY(8px)', 'important');
      });
      return;
    }

    this.externalFloatingStyleCache.forEach((cachedState, element) => {
      if (!element || !element.setAttribute) return;
      const style = typeof cachedState === 'string' ? cachedState : cachedState.style;
      const suppressed =
        typeof cachedState === 'string' ? '' : cachedState.suppressed;
      if (style) {
        element.setAttribute('style', style);
      } else {
        element.removeAttribute('style');
      }
      if (suppressed) {
        element.setAttribute('data-pb-floating-suppressed', suppressed);
      } else {
        element.removeAttribute('data-pb-floating-suppressed');
      }
    });
    this.externalFloatingStyleCache.clear();
  }

  shouldUseFixedScrollLock() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  getPageScrollState() {
    return { x: window.scrollX, y: window.scrollY };
  }

  rememberOpenScrollState() {
    if (!this.shouldPreservePageScroll()) return;
    this.pendingCartOpenScrollState = { ...this.getPageScrollState(), at: Date.now() };
  }

  getOpenScrollState() {
    if (!this.shouldPreservePageScroll()) return null;

    const pendingScrollState = this.pendingCartOpenScrollState;
    this.pendingCartOpenScrollState = null;

    if (pendingScrollState && Date.now() - pendingScrollState.at < 1000) {
      return { x: pendingScrollState.x, y: pendingScrollState.y };
    }

    return this.getPageScrollState();
  }

  restorePageScroll(scrollState) {
    if (!scrollState) return;
    window.scrollTo(scrollState.x, scrollState.y);
  }

  enforceLockedScrollPosition() {
    if (!this.pageScrollLock) return;
    if (window.scrollX === this.pageScrollLock.x && window.scrollY === this.pageScrollLock.y) return;

    this.restorePageScroll(this.pageScrollLock);
  }

  getLockedPageScrollState() {
    if (this.pageScrollLock) return { x: this.pageScrollLock.x, y: this.pageScrollLock.y };
    if (document.body.classList.contains('pb-cart-scroll-locked') || document.body.style.position === 'fixed') {
      const lockedTop = Number.parseFloat(document.body.style.top);
      const lockedLeft = Number.parseFloat(document.body.style.left);
      return {
        x: Number.isFinite(lockedLeft) ? Math.max(0, -lockedLeft) : window.scrollX,
        y: Number.isFinite(lockedTop) ? Math.max(0, -lockedTop) : window.scrollY,
      };
    }
    return this.getPageScrollState();
  }

  preventBackgroundScroll(event) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.contains(event.target)) return;

    event.preventDefault();
  }

  preventBackgroundKeyScroll(event) {
    const scrollKeys = [' ', 'ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp'];
    if (!scrollKeys.includes(event.key)) return;

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.contains(event.target)) return;

    event.preventDefault();
  }

  lockPageScroll(scrollState) {
    if (!scrollState || this.pageScrollLock) return;

    const useFixedLock = this.shouldUseFixedScrollLock();

    this.pageScrollLock = {
      x: scrollState.x,
      y: scrollState.y,
      fixed: useFixedLock,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };

    this.boundPreventBackgroundScroll = this.boundPreventBackgroundScroll || this.preventBackgroundScroll.bind(this);
    this.boundPreventBackgroundKeyScroll =
      this.boundPreventBackgroundKeyScroll || this.preventBackgroundKeyScroll.bind(this);
    this.boundEnforceLockedScrollPosition =
      this.boundEnforceLockedScrollPosition || this.enforceLockedScrollPosition.bind(this);
    document.addEventListener('wheel', this.boundPreventBackgroundScroll, { capture: true, passive: false });
    document.addEventListener('touchmove', this.boundPreventBackgroundScroll, { capture: true, passive: false });
    document.addEventListener('keydown', this.boundPreventBackgroundKeyScroll, true);
    window.addEventListener('scroll', this.boundEnforceLockedScrollPosition, { passive: true });

    document.body.classList.add('pb-cart-scroll-locked');
    if (!useFixedLock) return;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollState.y}px`;
    document.body.style.left = `-${scrollState.x}px`;
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  unlockPageScroll(scrollState) {
    this.setCartDrawerOpenState(false);

    if (this.boundPreventBackgroundScroll) {
      document.removeEventListener('wheel', this.boundPreventBackgroundScroll, true);
      document.removeEventListener('touchmove', this.boundPreventBackgroundScroll, true);
    }
    if (this.boundPreventBackgroundKeyScroll) {
      document.removeEventListener('keydown', this.boundPreventBackgroundKeyScroll, true);
    }
    if (this.boundEnforceLockedScrollPosition) {
      window.removeEventListener('scroll', this.boundEnforceLockedScrollPosition);
    }

    const lockState = this.pageScrollLock;
    const bodyWasLocked = document.body.classList.contains('pb-cart-scroll-locked') || document.body.style.position === 'fixed';
    const restoredScrollState = lockState ? { x: lockState.x, y: lockState.y } : scrollState;

    if (lockState) {
      document.body.style.position = lockState.position;
      document.body.style.top = lockState.top;
      document.body.style.left = lockState.left;
      document.body.style.right = lockState.right;
      document.body.style.width = lockState.width;
      document.body.style.overflow = lockState.overflow;
      document.body.classList.remove('pb-cart-scroll-locked');
      this.pageScrollLock = null;
    } else if (bodyWasLocked) {
      if (document.body.style.position === 'fixed') {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
      document.body.classList.remove('pb-cart-scroll-locked');
    }

    if (restoredScrollState) this.restorePageScroll(restoredScrollState);
    return restoredScrollState;
  }

  focusWithoutScroll(element) {
    if (!element) return;
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = Array.from(
      new Set(
        Array.from(
          document.querySelectorAll(
            '#cart-icon-bubble, #HeaderCart, .header_cart_info, .header__icon--cart, .header__icons a[href="/cart"], .header__icons a[href^="/cart?"], .header__icons a[href*="/cart"], [data-pb-unified-cart="true"]'
          )
        )
          .map((cartLink) => cartLink.closest('a, button') || cartLink)
          .filter(Boolean)
      )
    );
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.pbCartDrawerComponentBound === 'true') return;
      cartLink.dataset.pbDrawerBound = 'true';
      cartLink.dataset.pbCartDrawerComponentBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.addEventListener('pointerdown', () => this.rememberOpenScrollState(), { passive: true });
      cartLink.addEventListener('pointerdown', () => this.primeCartDrawerOpenState(), { passive: true });
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.__pbCartDrawerHandled = true;
        this.primeCartDrawerOpenState();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
          event.__pbCartDrawerHandled = true;
          this.rememberOpenScrollState();
          this.primeCartDrawerOpenState();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    const scrollState = this.getOpenScrollState();
    if (scrollState) {
      this.lockPageScroll(scrollState);
    }
    this.setCartDrawerOpenState(true);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');
    this.setAttribute('open', '');
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.setAttribute('open', '');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        if (scrollState) requestAnimationFrame(() => this.restorePageScroll(scrollState));
      },
      { once: true }
    );

    if (!scrollState || this.shouldUseFixedScrollLock()) {
      document.body.classList.add('overflow-hidden');
    }
    if (scrollState) {
      requestAnimationFrame(() => this.restorePageScroll(scrollState));
    }
  }

  close() {
    const scrollState = this.shouldPreservePageScroll() ? this.getLockedPageScrollState() : null;
    this.setCartDrawerOpenState(false);
    this.classList.remove('active');
    this.removeAttribute('open');
    if (scrollState) {
      removeTrapFocus();
      this.focusWithoutScroll(this.activeElement);
    } else {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    if (scrollState) {
      const restoredScrollState = this.unlockPageScroll(scrollState);
      requestAnimationFrame(() => {
        this.restorePageScroll(restoredScrollState);
        setTimeout(() => this.restorePageScroll(restoredScrollState), 50);
      });
    } else {
      this.unlockPageScroll();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.__pbCartDrawerAssetLoaded = true;
window.__pbOpenCartDrawer = function pbOpenCartDrawer(trigger, event) {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return false;

  if (event) {
    event.preventDefault();
    event.stopPropagation();
    event.__pbCartDrawerHandled = true;
  }

  const activeTrigger =
    trigger ||
    document.querySelector('#HeaderCart, .header_cart_info, #cart-icon-bubble, .header__icon--cart, [data-pb-unified-cart="true"]');

  drawer.rememberOpenScrollState?.();
  drawer.primeCartDrawerOpenState?.();
  drawer.open(activeTrigger);
  return true;
};

if (!window.__pbNativeCartAddDrawerBridgeLoaded) {
  window.__pbNativeCartAddDrawerBridgeLoaded = true;

  const isNativeCartAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.pbCartDrawerBypass === 'true') return false;
    if (form.closest('product-form')) return false;
    if (!form.querySelector('[name="add"], [type="submit"]')) return false;

    const action = form.getAttribute('action') || form.action || '';
    const cartAddPath = window.routes?.cart_add_url || '/cart/add';
    try {
      const actionUrl = new URL(action, window.location.origin);
      return actionUrl.pathname.replace(/\/$/, '') === cartAddPath.replace(/\/$/, '');
    } catch (error) {
      return action.includes('/cart/add');
    }
  };

  const getNativeCartAddSubmitter = (event, form) => {
    if (event.submitter instanceof HTMLElement) return event.submitter;
    return form.querySelector('[type="submit"], button[name="add"], input[name="add"]');
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isNativeCartAddForm(form)) return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function' || typeof drawer.getSectionsToRender !== 'function') {
        return;
      }

      event.preventDefault();

      const submitter = getNativeCartAddSubmitter(event, form);
      if (submitter?.getAttribute('aria-disabled') === 'true' || submitter?.disabled) return;

      const formData = new FormData(form);
      formData.append(
        'sections',
        drawer.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);

      drawer.setActiveElement?.(submitter || form);
      drawer.rememberOpenScrollState?.();
      drawer.primeCartDrawerOpenState?.();

      submitter?.setAttribute('aria-disabled', 'true');
      submitter?.classList.add('loading');

      fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const errorMessage = form.querySelector('.product-form__error-message');
            if (errorMessage) errorMessage.textContent = response.description || response.message || '';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'pb-native-cart-add-drawer-bridge',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }
          drawer.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
          form.dataset.pbCartDrawerBypass = 'true';
          form.submit();
        })
        .finally(() => {
          submitter?.classList.remove('loading');
          submitter?.removeAttribute('aria-disabled');
          if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
        });
    },
    true
  );
}

if (!window.__pbCartDrawerQtyDeleteBridgeLoaded) {
  window.__pbCartDrawerQtyDeleteBridgeLoaded = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      const deleteQuantityButton = target?.closest?.('cart-drawer .pb-cart-qty-delete');
      if (!deleteQuantityButton) return;

      const cartItem = deleteQuantityButton.closest('.cart-item');
      const removeControl = cartItem?.querySelector('cart-remove-button');
      if (!removeControl) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      removeControl.click();
    },
    true
  );
}

document.addEventListener(
  'click',
  (event) => {
    if (event.__pbCartDrawerHandled) return;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const cartTrigger = target?.closest?.(
      '#HeaderCart, .header_cart_info, #cart-icon-bubble, .header__icon--cart, [data-pb-unified-cart="true"]'
    );
    if (!cartTrigger || cartTrigger.closest?.('[data-pb-cart-duplicate="true"]')) return;
    window.__pbOpenCartDrawer(cartTrigger, event);
  },
  true
);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
